import { z } from 'zod/v4';

export const ControlTabKeys = [
  'details',
  'evidence',
  'risks',
  'findings',
  'comments',
  'tasks',
  'history',
  'relations',
  'settings',
] as const;
export const EvidenceTabKeys = [
  'versions',
  'details',
  'controls',
  'integrations',
  'comments',
  'tasks',
  'relations',
  'history',
] as const;
export const RiskTabKeys = [
  'details',
  'approvals',
  'riskLevel',
  'documents',
  'controls',
  'vendors',
  'tasks',
  'history',
  'comments',
  'reviews',
  'relations',
] as const;
export const VendorTabKeys = [
  'details',
  'approvals',
  'documents',
  'questionnaires',
  'risks',
  'tasks',
  'comments',
  'history',
  'reviews',
  'relations',
] as const;
export const QuestionnaireTabKeys = ['details', 'questions', 'vendors', 'comments'] as const;
export const VendorQuestionnairesTabKeys = [
  'details',
  'submissions',
  'findings',
  'comments',
] as const;
export const UserTabKeys = ['users', 'invitations'] as const;
export const AuditorTabKeys = ['auditors', 'invitations'] as const;
export const RoleTabKeys = ['permissions', 'users'] as const;
export const ApiAccessTabKeys = ['serviceAccounts', 'activityLog'] as const;
export const McpSettingsTabKeys = ['settings', 'connections'] as const;
export const CompanyTabKeys = ['email'] as const;
export const ServiceAccountTabKeys = ['overview', 'apiKeys', 'activity'] as const;
export const TaskTabKeys = ['details', 'comments', 'history', 'relations'] as const;
export const PolicyTabKeys = [
  'details',
  'approvals',
  'acknowledgement',
  'versions',
  'tasks',
  'comments',
  'history',
  'relations',
  'exceptions',
  'integrations',
] as const;
export const ClientQTabKeys = ['details', 'comments', 'history'] as const;
export const AccessTabKeys = ['accessGranted', 'accessRequest', 'accessClosed'] as const;

export type ControlTabKeys = (typeof ControlTabKeys)[number];
export type EvidenceTabKeys = (typeof EvidenceTabKeys)[number];
export type RiskTabKeys = (typeof RiskTabKeys)[number];
export type VendorTabKeys = (typeof VendorTabKeys)[number];
export type QuestionnaireTabKeys = (typeof QuestionnaireTabKeys)[number];
export type VendorQuestionnairesTabKeys = (typeof VendorQuestionnairesTabKeys)[number];
export type UserTabKeys = (typeof UserTabKeys)[number];
export type AuditorTabKeys = (typeof AuditorTabKeys)[number];
export type RoleTabKeys = (typeof RoleTabKeys)[number];
export type ApiAccessTabKeys = (typeof ApiAccessTabKeys)[number];
export type McpSettingsTabKeys = (typeof McpSettingsTabKeys)[number];
export type CompanyTabKeys = (typeof CompanyTabKeys)[number];
export type ServiceAccountTabKeys = (typeof ServiceAccountTabKeys)[number];
export type TaskTabKeys = (typeof TaskTabKeys)[number];
export type PolicyTabKeys = (typeof PolicyTabKeys)[number];
export type ClientQTabKeys = (typeof ClientQTabKeys)[number];
export type AccessTabKeys = (typeof AccessTabKeys)[number];

export type ControlDrawerOrigin = 'program_control' | 'control';

export const appDrawerSchema = z.object({
  drawerEntity: z
    .union([
      z.literal('control'),
      z.literal('task'),
      z.literal('evidence'),
      z.literal('risk'),
      z.literal('vendor'),
      z.literal('questionnaire'),
      z.literal('vendor-questionnaire'),
      z.literal('policy'),
      z.literal('policy-version'),
      z.literal('client-q-question'),
    ])
    .optional()
    .catch('control'),
  activeTab: z
    .union([
      z.enum(ControlTabKeys),
      z.enum(EvidenceTabKeys),
      z.enum(RiskTabKeys),
      z.enum(VendorTabKeys),
      z.enum(QuestionnaireTabKeys),
      z.enum(VendorQuestionnairesTabKeys),
      z.enum(UserTabKeys),
      z.enum(AuditorTabKeys),
      z.enum(RoleTabKeys),
      z.enum(ApiAccessTabKeys),
      z.enum(McpSettingsTabKeys),
      z.enum(CompanyTabKeys),
      z.enum(ServiceAccountTabKeys),
      z.enum(TaskTabKeys),
      z.enum(PolicyTabKeys),
      z.enum(ClientQTabKeys),
      z.enum(AccessTabKeys),
    ])
    .optional()
    .catch('details'),
  drawerEntityId: z.string().optional(),
  drawerOrigin: z.enum(['program_control', 'control']).optional().catch(undefined),
  approverId: z.string().optional(),
  autoApprove: z.boolean().optional(),
  openRequestApprovalModal: z.boolean().optional(),
  // The AI chat opened as a windowed overlay over the current page. Holds the
  // chat id; the global chat-modal host renders it. A hard load of the inbox
  // chat URL shows the full inbox page instead.
  chat: z.string().optional(),
});

export type AppDrawerType = z.infer<typeof appDrawerSchema>;
